'use client'

import React from "react"
import { useTranslations } from "@/components/I18nProvider"
import { GLOBAL_PATHS } from "@djotta/utils"
import { formatNumber } from "@/lib/libs"
import { useCardPricing } from "@/hooks/account/useCardPricing"
import { Button, Tabs, TabsList, TabsTrigger, TabsContent, Badge, Skeleton } from "@sarkiai/ui"
import { ShoppingCart, CheckCircle2, AlertCircle, RefreshCw, CreditCard, Bot, Building2, SwatchBook, User, Briefcase } from "lucide-react"
import Image from "next/image"
import { cn } from "@sarkiai/utils"


interface PricingSectionProps {
  showHeader?: boolean
  className?: string
}

interface PricingCardProps {
  t: any
  isLoading?: boolean
  isDark?: boolean
  /** Dynamic item from API */
  item?: any
  /** Static card props (for SarkiAI/Enterprise) */
  code?: string
  price?: string
  items?: string[]
  featured?: boolean
  cta?: string
  startingFrom?: boolean
}


export const PricingCard = ({
  t,
  isLoading = false,
  isDark = false,
  item,
  code,
  price,
  items,
  featured = false,
  cta,
  startingFrom = false
}: PricingCardProps) => {
  // Loading state
  if (isLoading || (!item && !code)) {
    return (
      <div className="min-h-[550px] w-full rounded-3xl bg-muted/30 border border-border/50 p-8 flex flex-col gap-6">
        <Skeleton className="h-12 w-12 rounded-2xl" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <div className="flex-1 space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <Skeleton className="h-14 w-full rounded-2xl" />
      </div>
    )
  }

  // Determine if this is a dynamic or static card
  const isStatic = !!code
  const cardCode = code || item?.code
  const cardItems = items || item?.list?.slice(0, 4).map((_: any, idx: number) => t(`${cardCode}.list_item_${idx + 1}`))
  const hasPrice = item?.card || price
  const showLoginLink = cardCode === 'sarki_pay_as_you_go'
  // Auto-feature premium cards
  const isFeatured = featured || cardCode === 'org_premium' || cardCode === 'per_vip'
  const showBadge = isFeatured || item?.shipping

  const isProd = process.env.NODE_ENV === "production"
  const authUrl = isProd ? "https://vcard.djotta.com/login" : "/auth/login"

  return (
    <div
      className={cn(
        "group relative rounded-[2.5rem] flex flex-col overflow-hidden transition-all duration-500 hover:shadow-2xl",
        !isStatic && "min-h-[550px] h-full",
        isDark
          ? "bg-white/10 border border-white/30 hover:border-white/50 text-white"
          : "bg-white dark:bg-card border border-black/15 dark:border-white/15 hover:border-black/30 dark:hover:border-white/30",
        isFeatured && "border-2 border-primary shadow-2xl shadow-primary/20 z-10"
      )}
    >
      {/* Popular Badge - Absolute top */}
      {isFeatured && (
        <div className="absolute -top-0 left-1/2 -translate-x-1/2 z-20">
          <Badge className="bg-primary text-primary-foreground border-none rounded-b-xl rounded-t-none px-4 py-1.5 text-[10px] uppercase tracking-widest border-none transition-all">
            {t('popular')}
          </Badge>
        </div>
      )}

      <div className={cn(
        "flex flex-col gap-6 relative z-10 h-full",
        isStatic ? "p-8" : "p-5",
        isFeatured && "pt-8",
        startingFrom && "p-5",
      )}>
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            <div className={cn(
              "w-12 h-12 rounded-2xl flex items-center justify-center mb-2 shadow-lg",
              isDark
                ? "bg-white text-black"
                : "bg-black dark:bg-white"
            )}>
              <SwatchBook className={cn("w-7 h-7", isDark ? "text-black" : "text-white dark:text-black")} />
            </div>
            <span className={cn(
              "text-[9px] uppercase tracking-widest opacity-60",
              isDark ? "text-white/60" : "text-muted-foreground"
            )}>
              {t(`${cardCode}.sup_title`)}
            </span>
          </div>
          {item?.shipping && (
            <Badge
              variant="outline"
              className={cn(
                "rounded-full text-[9px] uppercase tracking-widest",
                isDark
                  ? "bg-white/20 text-white backdrop-blur-md border-white/30"
                  : "bg-black/5 dark:bg-white/10 text-black dark:text-white border-black/10 dark:border-white/20"
              )}
            >
              {item.shipping}
            </Badge>
          )}
        </div>

        {/* Title */}
        <div>
          <h4 className="text-2xl uppercase tracking-tighter leading-none">
            {t(`${cardCode}.title`)}
          </h4>
          <p className={cn(
            "text-sm uppercase tracking-widest opacity-60 mt-1",
            isDark ? "text-white/70" : "text-muted-foreground"
          )}>
            {t(`${cardCode}.body_title`)}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-4">
          <p className={cn(
            "text-sm leading-relaxed",
            isStatic ? "font-medium" : "line-clamp-3",
            isDark ? "text-white/80" : "opacity-80"
          )}>
            {t(`${cardCode}.content`)}
          </p>
          <div className={cn("space-y-2", isStatic && "space-y-3")}>
            {cardItems?.map((itemText: string, idx: number) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle2 className={cn(
                  "h-4 w-4 shrink-0",
                  isDark ? "text-white" : "text-black dark:text-white"
                )} />
                <span className={cn(
                  "text-xs",
                  isDark ? "text-white/80" : "opacity-80"
                )}>
                  {isStatic ? itemText : t(`${cardCode}.list_item_${idx + 1}`)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer / Price */}
        <div className={cn(
          "space-y-2 mt-auto pt-4 border-t",
          isStatic && "space-y-4 mt-8 pt-6",
          isDark ? "border-white/20" : "border-black/5 dark:border-white/10"
        )}>
          <div className="flex flex-col">
            {/* Static price */}
            {isStatic && (
              <>
                {startingFrom && (
                  <span className="text-xs uppercase tracking-widest opacity-60 mb-1">
                    {t(`${cardCode}.starting_from`)}
                  </span>
                )}
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl">{price}</span>
                </div>
              </>
            )}

            {/* Dynamic price */}
            {!isStatic && item?.card && (
              <div className="flex flex-wrap items-baseline gap-1">
                <span className="text-xl uppercase tracking-widest">{item.card}</span>
                <span className={cn(
                  "text-sm font-medium",
                  isDark ? "text-white/60" : "opacity-60"
                )}>
                  {t('cards', { count: parseInt(item.card) })} /
                </span>
                <span className="text-2xl ml-1">
                  {formatNumber(item.packPrice)}
                </span>
                <span className={cn(
                  "text-xs uppercase tracking-widest opacity-60",
                  isDark ? "text-white/60" : "opacity-60"
                )}>
                  {t('xaf')}
                </span>
              </div>
            )}

            {/* Custom quote for items without price */}
            {!isStatic && !item?.card && (
              <span className="text-2xl uppercase tracking-tighter">{t('custom_quote')}</span>
            )}
          </div>

          <Button
            asChild
            className={cn(
              "w-full h-14 rounded-2xl transition-all text-xs uppercase tracking-widest border-none",
              isFeatured
                ? "bg-primary text-primary-foreground"
                : "bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
            )}
          >
            <a href={showLoginLink ? authUrl : "#contact-section"} className="flex items-center justify-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {cta || t(item?.card ? 'order' : 'contact_us')}
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}


interface PricingTabsProps {
  selectedTab: string
  onTabChange: (tab: string) => void
  orgPricing: any[]
  personalPricing: any[]
  isLoading: boolean
  t: any
}

export const PricingTabs: React.FC<PricingTabsProps> = ({
  selectedTab,
  onTabChange,
  orgPricing,
  personalPricing,
  isLoading,
  t
}) => {
  const tAppbar = useTranslations("appbar")

  return (
    <Tabs defaultValue="personal" value={selectedTab} onValueChange={onTabChange} className="w-full">
      <div className="flex justify-center mb-12">
        <TabsList className="bg-black/5 dark:bg-white/5 p-1.5 rounded-[1.5rem] h-auto border border-black/5 dark:border-white/5">
          <TabsTrigger
            value="personal"
            className="px-8 py-3 rounded-[1.25rem] font-bold uppercase tracking-widest text-[10px] data-[state=active]:bg-black dark:data-[state=active]:bg-white data-[state=active]:text-white dark:data-[state=active]:text-black transition-all gap-2"
          >
            <User className="w-4 h-4" />
            {t('personal_tab')}
          </TabsTrigger>
          <TabsTrigger
            value="business"
            className="px-8 py-3 rounded-[1.25rem] font-bold uppercase tracking-widest text-[10px] data-[state=active]:bg-black dark:data-[state=active]:bg-white data-[state=active]:text-white dark:data-[state=active]:text-black transition-all gap-2"
          >
            <Briefcase className="w-4 h-4" />
            {t('business_tab')}
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Personal Pricing Content */}
      <TabsContent value="personal" className="outline-none">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-stretch justify-center gap-8 w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-2xl mx-auto items-stretch justify-center">
            {/* SarkiAI Group */}
            <div className="space-y-10 flex flex-col items-center flex-1 h-full">
              <div className="text-center space-y-2">
                <h3 className="text-2xl uppercase tracking-tighter">SarkiAI</h3>
                <div className="h-1 w-12 bg-primary mx-auto rounded-full" />
              </div>
              <div className="flex justify-center h-full w-full flex-1 items-stretch">
                <PricingCard
                  t={t}
                  cta={tAppbar("login")}
                  code="sarki_pay_as_you_go"
                  price={t('sarki_pay_as_you_go.price')}
                  startingFrom
                  items={[
                    t('sarki_pay_as_you_go.list_item_1'),
                    t('sarki_pay_as_you_go.list_item_2'),
                    t('sarki_pay_as_you_go.list_item_3'),
                    t('sarki_pay_as_you_go.list_item_4')
                  ]}
                />
              </div>
            </div>

            {/* VCard Group */}
            <div className="space-y-10 flex flex-col items-center flex-[2] h-full">
              <div className="text-center space-y-2">
                <h3 className="text-2xl uppercase tracking-tighter">VCard</h3>
                <div className="h-1 w-12 bg-primary mx-auto rounded-full" />
              </div>
              <div className="grid grid-cols-1 w-full items-stretch justify-stretch flex-1">
                {(isLoading ? Array.from(new Array(1)) : personalPricing).map((item, index) => (
                  <div key={`personal-vcard-wrapper-${index}`} className="flex justify-center h-full w-full">
                    <PricingCard item={item} t={t} isLoading={isLoading} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </TabsContent>

      {/* Business Pricing Content */}
      <TabsContent value="business" className="space-y-20 outline-none">
        {/* SAE Section */}
        <div className="space-y-10 flex flex-col items-center justify-center">
          <div className="text-center space-y-2">
            <h3 className="text-2xl uppercase tracking-tighter">SarkiAI Enterprise Agent</h3>
            <div className="h-1 w-12 bg-primary mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 w-full max-w-md mx-auto items-center justify-center">
            <PricingCard
              t={t}
              featured
              code="enterprise_custom"
              price={t('enterprise_custom.price')}
              items={[
                t('enterprise_custom.list_item_1'),
                t('enterprise_custom.list_item_2'),
                t('enterprise_custom.list_item_3'),
                t('enterprise_custom.list_item_4'),
                t('enterprise_custom.list_item_5')
              ]}
              cta={t('contact_us')}
            />
          </div>
        </div>

        {/* VCard Section - 4 items in one row */}
        <div className="space-y-10 flex flex-col items-center justify-center pt-10 border-t border-black/5 dark:border-white/10">
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-black uppercase tracking-tighter">VCard</h3>
            <div className="h-1 w-12 bg-primary mx-auto rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-7xl mx-auto items-stretch justify-center">
            {(isLoading ? Array.from(new Array(4)) : orgPricing).map((item, index) => (
              <div key={`business-vcard-wrapper-${index}`} className="flex justify-center h-full w-full">
                <PricingCard item={item} t={t} isLoading={isLoading} />
              </div>
            ))}
          </div>
        </div>
      </TabsContent>
    </Tabs>
  )
}

const PricingSection: React.FC<PricingSectionProps> = ({
  showHeader = true,
  className
}) => {
  const t = useTranslations("pricing")
  const commonT = useTranslations("common")

  const [selectedTab, setSelectedTab] = React.useState("business")

  const { orgPricing, personalPricing, isLoading, error, refetch } = useCardPricing()

  return (
    <section id="pricing-section" className={cn("py-16 relative overflow-hidden px-4 lg:px-12", className)}>
      <div className="container mx-auto">
        {showHeader && (
          <div className="flex flex-col items-center mb-12">
            <h2 className="text-3xl md:text-4xl uppercase tracking-tighter leading-none text-center text-foreground">
              {t('title')}
            </h2>
            <p className="mt-4 text-lg text-center text-muted-foreground max-w-2xl">
              {t('subtitle')}
            </p>
          </div>
        )}

        {error ? (
          <div className="flex flex-col items-center gap-4 p-8 border border-destructive/20 rounded-3xl bg-destructive/5 mb-8">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <span className="font-medium">{error}</span>
            </div>
            <Button variant="outline" onClick={refetch} className="gap-2 rounded-full">
              <RefreshCw className="h-4 w-4" />
              {commonT('retry')}
            </Button>
          </div>
        ) : (
          <PricingTabs
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            orgPricing={orgPricing}
            personalPricing={personalPricing}
            isLoading={isLoading}
            t={t}
          />
        )}
      </div>
    </section>
  )
}

export default PricingSection
